// src/modules/brands/services/brand.service.ts
import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";
import {
  API_URL,
  searchUsersUrl,
  API_HEADER,
} from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import MediaService from "@modules/media/services/media.service";
import {
  BrandDto,
  BrandHighlightDto,
  CreateBrandInput,
  UpdateBrandInput,
  UserInfo,
  mapBrandDto,
  mapBrandHighlightDto,
} from "../models/brand.model";
import {
  fetchAllPaginated,
  buildPagingQuery,
  shouldContinuePaging,
  extractList,
} from "@modules/shared/services/pagination.service";

type AuthArgs = {
  auth: AuthenticationEntity | null;
  setAuth: (auth: AuthenticationEntity | null) => void;
  logout: () => void;
};

export class BrandService {
  private readonly mediaService = new MediaService();
  private getHeaders(): HeadersInit {
    return {
      ...API_HEADER,
    };
  }

  private getFormHeaders(): HeadersInit {
    const headers = this.getHeaders() as Record<string, string>;
    const { "Content-Type": _, ...rest } = headers;
    return rest;
  }

  private async uploadFile(file: File, args: AuthArgs): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.mediaService.uploadFile(
      formData,
      args.auth,
      args.setAuth,
      args.logout,
    );
    return (response as any)?.data?.url ?? (response as any)?.url ?? "";
  }

  // === User ===
  async getUserById(userId: string, args: AuthArgs): Promise<UserInfo | null> {
    if (!userId) return null;
    try {
      const response = await fetchWithAuth(
        `${API_URL.USER_BY_ID}/${encodeURIComponent(userId)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const user = response?.data ?? response;
      if (user && typeof user === "object") {
        let avatarUrl = "";
        if (Array.isArray(user.avatars) && user.avatars.length > 0) {
          avatarUrl = user.avatars[0].url || "";
        }
        return {
          id: user.id ?? user._id ?? userId,
          name: user.name ?? user.fullName ?? user.username ?? "",
          email: user.email ?? "",
          avatar: avatarUrl,
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  async searchUsers(keyword: string, args: AuthArgs): Promise<UserInfo[]> {
    if (!keyword.trim()) return [];

    try {
      const normalizedKeyword = keyword
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "d")
        .replace(/Đ/g, "D");

      const response = await fetchWithAuth(
        searchUsersUrl(normalizedKeyword),
        {
          method: "POST",
          headers: {
            ...this.getHeaders(),
            "x-language": "vi",
          },
        },
        args.auth,
        args.setAuth,
        args.logout,
      );

      const users = extractList<any>(response);
      return users
        .map((user: any) => {
          let avatarUrl = "";
          if (Array.isArray(user.avatars) && user.avatars.length > 0) {
            avatarUrl = user.avatars[0].url || "";
          }
          const id = String(user.id ?? user._id ?? "");
          return {
            id,
            name: user.name ?? user.fullName ?? user.username ?? "",
            email: user.email ?? user.username ?? "",
            avatar: avatarUrl,
          };
        })
        .filter((user) => user.id);
    } catch (error) {
      console.error("searchUsers error", error);
      return [];
    }
  }

  // === Brand ===
  async getAllBrands(
    args: AuthArgs,
    businessModel?: string,
  ): Promise<BrandDto[]> {
    return fetchAllPaginated<BrandDto>(async (page, limit) => {
      let url = `${API_URL.B2B}?${buildPagingQuery(page, limit, -1)}`;
      if (businessModel) {
        url += `&business_model=${businessModel}`;
      }
      const response = await fetchWithAuth(
        url,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapBrandDto);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  // 👇 Thêm method này
  async getBrandById(id: string, args: AuthArgs): Promise<BrandDto | null> {
    try {
      const response = await fetchWithAuth(
        `${API_URL.B2B}/${encodeURIComponent(id)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      return mapBrandDto(response.data ?? response);
    } catch {
      return null;
    }
  }

  async createBrand(
    input: CreateBrandInput,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      API_URL.B2B,
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({
          contract_id: input.contract_id.trim(),
          name: input.name.trim(),
          contact_phone: input.contact_phone.trim(),
          contact_email: input.contact_email.trim(),
          ...(input.owner_id?.trim()
            ? { owner_id: input.owner_id.trim() }
            : {}),
          description: input.description.trim(),
          show_social: input.show_social ?? false,
          scan_record: input.scan_record || [],
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async updateBrand(
    id: string,
    input: UpdateBrandInput,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const body: any = {};
    if (input.name !== undefined) body.name = input.name?.trim();
    if (input.description !== undefined)
      body.description = input.description?.trim();
    if (input.show_social !== undefined) body.show_social = input.show_social;
    if (input.contact_phone !== undefined)
      body.contact_phone = input.contact_phone?.trim();
    if (input.contact_email !== undefined)
      body.contact_email = input.contact_email?.trim();
    if (input.contract_id !== undefined)
      body.contract_id = input.contract_id?.trim();
    if (input.owner_id !== undefined) body.owner_id = input.owner_id?.trim();
    if (input.scan_record !== undefined) {
      body.scan_record = input.scan_record;
    }

    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify(body),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async deleteBrand(id: string, args: AuthArgs): Promise<boolean> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}`,
      { method: "DELETE", headers: this.getHeaders() },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return typeof response === "boolean"
      ? response
      : Boolean(response?.success ?? response);
  }

  async addBrandMember(
    id: string,
    memberId: string,
    branchId: string,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/member`,
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({
          user_id: memberId.trim(),
          branch_id: branchId.trim(),
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async removeBrandMember(
    id: string,
    memberId: string,
    branchId: string,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/member`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify({
          user_id: memberId,
          branch_id: branchId,
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async uploadBrandLogo(
    id: string,
    file: File,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/logo`,
      { method: "POST", headers: this.getFormHeaders(), body: formData },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async uploadBrandBanner(
    id: string,
    file: File,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/banner`,
      { method: "POST", headers: this.getFormHeaders(), body: formData },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async addBrandMedia(
    id: string,
    file: File,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/media`,
      { method: "POST", headers: this.getFormHeaders(), body: formData },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async removeBrandMedia(
    id: string,
    key: string,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/media`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify({ key }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async removeBrandLogo(
    id: string,
    key: string,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/logo`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify({ key }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  async removeBrandBanner(
    id: string,
    key: string,
    args: AuthArgs,
  ): Promise<BrandDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/${encodeURIComponent(id)}/banner`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify({ key }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandDto(response.data ?? response);
  }

  // === Highlights ===
  async getHighlights(args: AuthArgs): Promise<BrandHighlightDto[]> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/highlight/all`,
      { method: "GET", headers: this.getHeaders() },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return extractList(response).map(mapBrandHighlightDto);
  }

  async addHighlight(
    b2bId: string,
    args: AuthArgs,
  ): Promise<BrandHighlightDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/highlight`,
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({ b2b_id: b2bId }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandHighlightDto(response.data ?? response);
  }

  async updateHighlightIndex(
    b2bId: string,
    orderIndex: number,
    args: AuthArgs,
  ): Promise<BrandHighlightDto> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/highlight/index`,
      {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ b2b_id: b2bId, order_index: orderIndex }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapBrandHighlightDto(response.data ?? response);
  }

  async removeHighlight(b2bId: string, args: AuthArgs): Promise<boolean> {
    const response = await fetchWithAuth(
      `${API_URL.B2B}/highlight/delete`,
      {
        method: "DELETE",
        headers: this.getHeaders(),
        body: JSON.stringify({ b2b_id: b2bId }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return typeof response === "boolean"
      ? response
      : Boolean(response?.success ?? response);
  }

  async uploadMediaFile(
    file: File,
    args: AuthArgs,
  ): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.mediaService.uploadFile(
      formData,
      args.auth,
      args.setAuth,
      args.logout,
    );
    return {
      url: (response as any)?.data?.url ?? (response as any)?.url ?? "",
      key: (response as any)?.data?.key ?? (response as any)?.key ?? "",
    };
  }
}
